import type { CharacterManifest } from "@arena/shared";
import type { MuseumObject } from "@arena/arena-data";
import { isTyping } from "../input/controller.ts";
import { championCard } from "./championCard.ts";

/** What the museum's controls show this frame (D49). */
export interface MuseumHudView {
  /** In the arena, training; else in the museum. */
  inArena: boolean;
  ghost: boolean;
  isHost: boolean;
  /** Everyone has a champion: the host may start. */
  allPicked: boolean;
  /** Players and bots in the room: a match needs two. */
  players: number;
  /** The object within reach, if any. */
  usable: MuseumObject | null;
  /** This player's champion ("" while a ghost). */
  mine: string;
  /** Who holds each champion, by champion id. */
  holders: ReadonlyMap<string, string>;
}

/** How long a line of chat stays readable in the log. */
const CHAT_SECONDS = 14;
const CHAT_LINES = 6;

/**
 * The museum's controls (D49): "Usa" near an object (F on a keyboard), the
 * card of a statue with its champion's sheet and the button that takes it,
 * a line of advice, the way back from the arena, and the chat. Every action
 * is a request: the server decides, the replicated state shows the outcome.
 */
export class MuseumHud {
  onPick: (championId: string) => void = () => {};
  /** A statue's card opens: its champion's voice, if they have one. */
  onLook: (championId: string) => void = () => {};
  onDesk: () => void = () => {};
  onTrain: (on: boolean) => void = () => {};
  onSay: (text: string) => void = () => {};
  onStart: () => void = () => {};

  private readonly root = element("museum");
  private readonly hint = element("museum-hint");
  private readonly use = element<HTMLButtonElement>("museum-use");
  private readonly back = element<HTMLButtonElement>("museum-back");
  private readonly train = element<HTMLButtonElement>("museum-train");
  private readonly start = element<HTMLButtonElement>("museum-start");
  private readonly card = element("statue-card");
  private readonly cardInfo = element("statue-info");
  private readonly cardStatus = element("statue-status");
  private readonly cardPick = element<HTMLButtonElement>("statue-pick");
  private readonly chatLog = element<HTMLOListElement>("chat-log");
  private readonly chatForm = element<HTMLFormElement>("chat-form");
  private readonly chatInput = element<HTMLInputElement>("chat-input");
  private readonly champions: readonly CharacterManifest[];
  private view: MuseumHudView | null = null;
  /** The statue whose card is open. */
  private open: number | null = null;

  constructor(champions: readonly CharacterManifest[]) {
    this.champions = champions;
    this.use.addEventListener("click", () => this.useObject());
    this.back.addEventListener("click", () => this.onTrain(false));
    this.train.addEventListener("click", () => this.onTrain(true));
    this.start.addEventListener("click", () => this.onStart());
    element("statue-close").addEventListener("click", () => this.closeCard());
    this.cardPick.addEventListener("click", () => {
      const champion = this.open === null ? undefined : this.champions[this.open];
      if (champion) this.onPick(champion.id);
      this.closeCard();
    });
    element("chat-open").addEventListener("click", () => this.openChat());
    this.chatForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = this.chatInput.value.trim();
      if (text) this.onSay(text);
      this.closeChat();
    });
    window.addEventListener("keydown", (e) => {
      if (!this.view || e.repeat) return;
      if (e.code === "Escape") {
        this.closeCard();
        this.closeChat();
        return;
      }
      if (isTyping(e)) return;
      if (e.code === "KeyF" && this.open === null) this.useObject();
      else if (e.code === "KeyT" || e.code === "Enter") {
        e.preventDefault();
        this.openChat();
      }
    });
  }

  /** While reading a statue's card or typing, the fighter stands still. */
  get busy(): boolean {
    return this.open !== null || !this.chatForm.hidden;
  }

  /** `null`: not in the lobby, or the register is open over everything. */
  update(view: MuseumHudView | null): void {
    this.view = view;
    this.root.hidden = view === null;
    document.body.classList.toggle("in-museum", view !== null && !view.inArena);
    if (!view) {
      this.closeCard();
      this.closeChat();
      return;
    }
    this.back.hidden = !view.inArena;
    this.train.hidden = view.inArena || view.ghost;
    // No ghost left: the host starts, then the countdown runs.
    this.start.hidden = !(view.isHost && view.allPicked && view.players >= 2);
    const label = view.usable ? useLabel(view.usable, this.champions) : "";
    this.use.hidden = view.usable === null || this.open !== null;
    if (this.use.textContent !== label) this.use.textContent = label;
    const hint = hintFor(view);
    if (this.hint.textContent !== hint) this.hint.textContent = hint;
    if (this.open !== null) this.showCard(this.open);
  }

  /** A line of chat: in the log for a while (the bubble over the head is the fighter view's). */
  heard(name: string, color: string, text: string): void {
    const line = document.createElement("li");
    const who = document.createElement("b");
    who.textContent = `${name}: `;
    who.style.color = color;
    line.append(who, text);
    this.chatLog.append(line);
    while (this.chatLog.children.length > CHAT_LINES) this.chatLog.firstElementChild?.remove();
    window.setTimeout(() => line.classList.add("old"), CHAT_SECONDS * 1000);
  }

  private useObject(): void {
    const view = this.view;
    const object = view?.usable;
    if (!view || !object) return;
    if (object.kind === "statue") this.showCard(object.index);
    else this.onDesk();
  }

  private showCard(index: number): void {
    const champion = this.champions[index];
    const view = this.view;
    if (!champion || !view) return;
    const holder = view.holders.get(champion.id);
    const mine = view.mine === champion.id;
    const status = mine ? "È il tuo campione." : holder ? `In prestito a ${holder}: scegline un altro.` : view.ghost ? "Libero: toccalo e diventa lui." : "Libero: puoi cambiare campione.";
    if (this.open !== index) {
      this.open = index;
      this.card.style.setProperty("--accent-color", champion.look.accent);
      this.cardInfo.replaceChildren(...championCard(champion));
      this.card.hidden = false;
      document.body.classList.add("statue-open");
      this.onLook(champion.id);
    }
    if (this.cardStatus.textContent !== status) this.cardStatus.textContent = status;
    this.cardPick.disabled = mine || holder !== undefined;
    const pick = `Diventa ${champion.displayName}`;
    if (this.cardPick.textContent !== pick) this.cardPick.textContent = pick;
  }

  private closeCard(): void {
    this.open = null;
    this.card.hidden = true;
    document.body.classList.remove("statue-open");
  }

  private openChat(): void {
    if (!this.view) return;
    this.chatForm.hidden = false;
    this.chatInput.focus();
  }

  private closeChat(): void {
    this.chatInput.value = "";
    this.chatInput.blur();
    this.chatForm.hidden = true;
  }
}

function useLabel(object: MuseumObject, champions: readonly CharacterManifest[]): string {
  if (object.kind === "statue") return `Guarda: ${champions[object.index]?.displayName ?? "statua"}`;
  return "Apri il registro";
}

function hintFor(view: MuseumHudView): string {
  if (view.inArena) return "Allenamento: qui i colpi contano. Torna al museo quando vuoi.";
  if (view.ghost) return "Sei un fantasma: avvicinati a una statua e premi Usa (F) per prendere quel campione.";
  if (view.isHost && view.players < 2) return "Invita qualcuno (o aggiungi un bot dal registro): una partita si gioca in due.";
  if (view.isHost) return view.allPicked
    ? "Nessun fantasma in sala: puoi avviare la partita."
    : "Aspetta che ogni fantasma prenda un campione, poi avvia la partita.";
  return "Regole e mappa sono nel registro del curatore. T per scrivere.";
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} mancante in index.html`);
  return el as T;
}
